import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAlleNachrichten } from '../../hooks/buch'
import { useHighlights, useMembers, useTracks, useTrip } from '../../hooks/queries'
import { ausschnittAus, bildStil } from '../../lib/ausschnitt'
import { Buchkarte } from './Buchkarte'
import { useBuchquelle } from './buchquelle'
import { imZeitraum, istGesetzt, zeitraumAus, zeitraumText } from './zeitraum'
import { Beitrag, Punktseite } from './Buchteile'
import { Tagestrenner } from '../thread/Tagestrenner'
import { buchGliederung } from '../../lib/buchkapitel'
import { ExportButtons } from './ExportButtons'
import { tripPeriod } from '../../lib/format'
import { datumText } from '../../lib/ortszeit'
import { BackIcon } from '../../components/Icons'
import { Netzbild } from '../../components/Netzbild'
import { useTexte } from '../../texte'
import '../../styles/buch.css'

/**
 * Die Reise als Buch (#14).
 *
 * **Antworten stehen nicht darin, und das entscheidet nicht diese Seite.**
 * `Visibility::scope()` gibt dem Absender ohne `?with=` ausschließlich
 * `kind = 'broadcast'` zurück — hier wird deshalb nichts gefiltert. Die eine
 * Regel bleibt an der einen Stelle; ein Filter hier wäre eine zweite, und die
 * zweite läuft irgendwann auseinander.
 *
 * **The server builds the file** (since 0.30.0): PDF or EPUB, with photos,
 * videos and the maps from this page. The page stays as the preview -- and
 * as the place where the maps are drawn that the server cannot draw.
 * Printing still works via the browser's menu; the rules for it are in
 * `styles/buch.css`.
 */
export function BuchPage() {
  const { tripId } = useParams()
  const id = Number(tripId)
  const [suche] = useSearchParams()

  const trip = useTrip(id)
  const punkte = useHighlights(id, true)
  const spuren = useTracks(id, true)
  const empfaenger = useMembers(id, true)
  const stand = useAlleNachrichten(id)

  /**
   * **Welche Karten schon fertig sind — als Menge und nicht als Zahl.**
   *
   * Es ist nicht mehr eine Karte, sondern die Übersicht **und** ein kleiner
   * Ausschnitt an jedem Punkt. Ein Zähler wäre die naheliegende Lösung und die
   * falsche: meldete eine Karte zweimal (ein Neuzeichnen genügt), stünde der
   * Knopf offen, während eine andere noch grau ist. Eine Menge zählt jede nur
   * einmal, und zwar die richtige.
   */
  const [fertig, setFertig] = useState<ReadonlySet<string>>(new Set())
  const melde = useCallback((was: string) => {
    setFertig((alt) => (alt.has(was) ? alt : new Set(alt).add(was)))
  }, [])

  const versatz = trip.data?.zones ?? null

  /**
   * Der Zeitraum schneidet zu — und er schneidet **alles** zu, nicht nur die
   * Beiträge. Ein Buch, in dem die Karte die ganze Reise zeigt und der Text
   * eine Woche, wäre zwei Bücher in einem.
   *
   * Die Rechnung selbst steht in `zeitraum.ts`, weil das private Dokument und
   * die Infoseite dieselbe brauchen. Sie hing hier als `useMemo` und galt
   * damit nur für diese Seite.
   */
  const zeitraum = useMemo(() => zeitraumAus(suche), [suche])
  const passt = useMemo(() => imZeitraum(zeitraum, versatz), [zeitraum, versatz])
  const t = useTexte().buch

  const beitraege = stand.nachrichten.filter((m) => passt(m.created_at))
  const gezeigtePunkte = (punkte.data?.highlights ?? [])
    .filter((p) => p.published_at !== null)
    .filter((p) => passt(p.happened_at))

  // Der Titel des Fensters wird im Druckdialog zum Dateinamen -- deshalb steht
  // dort der Name der Reise und nicht „Travel Broadcast".
  useEffect(() => {
    const alt = document.title
    if (trip.data !== undefined) document.title = trip.data.title
    return () => {
      document.title = alt
    }
  }, [trip.data])

  const quelle = useBuchquelle(id)

  /*
   * **Gewartet wird auf die, die wirklich dastehen.** Die Übersichtskarte gibt
   * es nur, wenn es Punkte oder eine Route gibt; einen Ausschnitt gibt es je
   * gezeigtem Punkt. Wer auf eine Karte wartet, die gar nicht gezeichnet wird,
   * lässt den Druckknopf für immer zu.
   */
  const zeigtRoute = gezeigtePunkte.length > 0 || (spuren.data?.tracks?.length ?? 0) > 0
  const noetig = [
    ...(zeigtRoute ? ['route'] : []),
    ...gezeigtePunkte.map((p) => `punkt:${p.id}`),
  ]
  /*
   * **And not before the points and tracks have answered** (1.83.0). The
   * list above is built from what has arrived: while the highlights are still
   * on their way, it names no map at all, and "every map is done" is true
   * over nothing. Measured on the test bench with four server workers, where
   * the highlights can answer after the messages: the button opened on a book
   * without a single point. A pending request holds the button; a failed one
   * does not, so a server that cannot list the points still lets the text
   * through, as before.
   */
  const geladen = !punkte.isPending && !spuren.isPending
  const bereit = !stand.laeuft && geladen && noetig.every((k) => fertig.has(k))

  return (
    /*
     * **Die Leiste steht neben dem Scrollkasten, nicht darin.**
     *
     * Sie war ein `position: sticky` innerhalb von `.buch` -- und damit im
     * Kasten, den sie überdeckt. Auf iOS ruckelt so eine Kopfzeile im
     * Schwungscrollen, und weil `.buch` einen Innenabstand hat, scrollte der
     * Inhalt links und rechts sichtbar an ihr vorbei. Gemeldet als „komische
     * Effekte" beim Scrollen. Jetzt sind es zwei Geschwister in einer
     * Flex-Spalte -- so wie überall sonst in dieser Anwendung.
     */
    <div className="buchseite">
      <div className="buch__leiste buch__nurbildschirm">
        <Link className="icon-button" to={`/t/${id}/info`} aria-label={t.zurueck}>
          <BackIcon />
        </Link>
        <span className="bar__grow">
          {stand.laeuft
            ? t.zusammenstellen(stand.seiten)
            : t.umfang(beitraege.length, gezeigtePunkte.length)}
        </span>
        {stand.fehler !== null && (
          <p className="notice notice--eng" role="alert">
            {stand.fehler}
          </p>
        )}
        {/* **The server builds the file** (#14, since 0.30.0). The maps here are
            drawn for it: the export copies them and sends them along. */}
        <ExportButtons
          tripId={id}
          from={zeitraum.von}
          to={zeitraum.bis}
          ready={bereit && stand.fehler === null}
          waitingText={stand.laeuft ? t.nochNichtFertig : t.nichtBereit}
        />
      </div>
      {/* **The media as a ZIP stood here** (#75) and does not any more (1.11.0,
          from use: "den Button „Medien exportieren" aus der Buchseite
          rausnehmen"). It is on the trip page, where the book is opened from.
          A link to the statistics stood here for one version and went the
          same way (1.12.0): the bar carries the book, nothing else. */}

      {/* **Die eigene Fußzeile.** Sie wiederholt sich beim Drucken auf jeder
          Seite; Safaris eigene Zeile mit Adresse und Seitenzahl steht daneben
          im Seitenrand und lässt sich nur im Druckdialog abschalten. */}
      <p className="buch__fuss">{trip.data?.title ?? t.reise}</p>

      <div className="buch">
        <section className="buch__deckel">
          {trip.data?.cover_url != null && (
            <span className="buch__deckelbild">
              <Netzbild
                src={trip.data.cover_url}
                style={bildStil(ausschnittAus(trip.data.cover_crop))}
              />
            </span>
          )}
          <h1>{trip.data?.title ?? t.reise}</h1>
          <p>{tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)}</p>
          <p>{trip.data?.owner.display_name}</p>
          {/* **Ein Ausschnitt sagt, dass er einer ist.** Sonst hält das Buch
              in ein paar Jahren jemand für den ganzen Bericht und sucht
              vergeblich nach dem Rest. */}
          {istGesetzt(zeitraum) && (
            <p className="buch__ausschnitt">
              <strong>{t.ausschnitt(zeitraumText(zeitraum))}</strong>
            </p>
          )}
        </section>

        {(gezeigtePunkte.length > 0 || (spuren.data?.tracks?.length ?? 0) > 0) && (
          <section className="buch__abschnitt">
            <h2>{t.route}</h2>
            <Buchkarte
              tripId={id}
              tracks={spuren.data?.tracks ?? []}
              punkte={gezeigtePunkte}
              onFertig={() => melde('route')}
            />
          </section>
        )}

        <section className="buch__abschnitt">
          <h2>{t.bericht}</h2>
          {/* **Ohne Zeitraum ist „in diesem Zeitraum" die falsche Auskunft.**
              Der Satz stand fest da und behauptete einen Ausschnitt, den
              niemand gewählt hatte. */}
          {beitraege.length === 0 && (
            <p>
              {istGesetzt(zeitraum) ? t.keinBeitragImZeitraum : t.keinBeitrag}
            </p>
          )}
          {/* **Ein Buch kennt kein „Heute".** `buchTag` rechnet in der Uhr
              der Reise, nicht des Geräts. **Und seit den Tagestrennern
              (#113) gliedert `buchGliederung()`** -- dieselbe Regel wie
              `Content::chapters()` im Server: ein Trenner eröffnet ein
              Kapitel, die Tageszeile steht darin nur bei einem anderen
              Schreibtag, ohne Trenner bleibt alles wie bisher. */}
          {buchGliederung(beitraege, versatz).map(({ trenner, tagZeile }, i) => {
            const nachricht = beitraege[i]!
            return (
              <div key={nachricht.id}>
                {trenner !== null && <Tagestrenner trenner={trenner} buch />}
                {tagZeile !== null && <p className="buch__tag">{tagZeile}</p>}
                <Beitrag nachricht={nachricht} versatz={versatz} />
              </div>
            )
          })}
        </section>

        {gezeigtePunkte.length > 0 && (
          <section className="buch__abschnitt">
            <h2>{t.punkte}</h2>
            {/* **Der Lizenzvermerk für die kleinen Ausschnitte — einmal, und
                nicht von Hand geschrieben.** Er kommt aus derselben festen
                Liste, aus der Leaflet ihn an der Übersichtskarte bezieht
                (`MapSources`); wer die Quelle wechselt, wechselt ihn
                zwangsläufig mit. Als HTML, weil er eines ist (`&copy;`) — und
                es ist kein frei eingetippter Text, sondern einer von zwei
                festen Einträgen. */}
            <p
              className="buch__quelle buch__punkt-zeile"
              dangerouslySetInnerHTML={{ __html: t.kartenausschnitte(quelle.attribution) }}
            />
            {gezeigtePunkte.map((punkt, i) => (
              <Punktseite
                key={punkt.id}
                punkt={punkt}
                nummer={i + 1}
                tripId={id}
                reiseVersatz={versatz}
                onKarteFertig={() => melde(`punkt:${punkt.id}`)}
              />
            ))}
          </section>
        )}

        <section className="buch__abschnitt">
          {/* **Der Stand des Exporttags, und die Seite sagt es.** Wer wann dabei
              war, ließe sich aus `join_requests` rekonstruieren -- das wäre ein
              eigenes Vorhaben und für ein Buch die falsche Genauigkeit. */}
          <h2>{t.empfaenger}</h2>
          <p className="buch__stand">{t.stand(datumText(new Date().toISOString(), versatz))}</p>
          <ul className="buch__liste">
            {(empfaenger.data ?? [])
              .filter((m) => m.role === 'recipient' && m.left !== true)
              .map((m) => (
                <li key={m.user.id}>{m.user.display_name}</li>
              ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
